/**
 * Pine Park Health Design System - Color Tokens
 * 
 * These colors match the Figma design system tokens.
 * Reference: Figma Tokens panel → "Primary Colors", "Functional Colors", and "Grays"
 */

export const colors = {
  // Primary Brand Colors (from Figma "Primary Colors")
  brand: {
    50: '#F2F8F6',  // Mint Cream - Very light green tint backgrounds
    100: '#D7EAE3', // Honeydew - Light green for selected states/cards
    400: '#4D8C78', // Viridian - Icons, secondary green elements
    750: '#115C44', // Castleton (Green) - Active states, buttons, links
    850: '#173F32', // Forest Green - Primary brand, sidebar, logo bg
  },
  
  // Chartreuse scale (from Figma "Functional Colors")
  chartreuse: {
    50: '#FBFBEF',  // Lightest chartreuse
    100: '#F6F7DE', // Light chartreuse
    200: '#E9EDB2', // Soft chartreuse - tag backgrounds
    300: '#DBE280', // Citron - highlights
  },
  
  // Amber scale (from Figma "Functional Colors")
  amber: {
    50: '#FFFBEB',  // Lightest amber - softest warning backgrounds
    300: '#FEF3C7', // Light amber - warning backgrounds
    900: '#78350F', // Dark amber - warning text
  },
  
  // Red scale (from Figma "Functional Colors")
  red: {
    50: '#FEF2F2',  // Lightest red - error backgrounds
    100: '#FEE2E2', // Light red - error backgrounds
    900: '#7F1D1D', // Dark red - error text
  },
  
  // Navy (from Figma "Functional Colors")
  navy: '#0B0D32',
  
  // Semantic Colors for UI elements
  blue: {
    800: '#1e40af', // AWV Needed badge text color
  },
  
  // Neutral Colors
  white: '#FFFFFF',
  
  // Gray scale (from Figma "Grays")
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280', // Secondary text
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827', // Primary text
  },
  
  // Legacy semantic colors (consider using amber/red scales instead)
  success: {
    500: '#22c55e',
    600: '#16a34a',
  },
  
  warning: {
    500: '#f59e0b',
    600: '#d97706',
  },
  
  error: {
    500: '#ef4444',
    600: '#dc2626',
  },
  
  // Status Colors for visits
  status: {
    confirmed: '#115C44',  // Castleton (brand[750])
    committed: '#115C44',  // Castleton (brand[750])
    overdue: '#374151',    // Gray-700
  },
} as const;

// Type for accessing nested color values
export type ColorKeys = keyof typeof colors;

// Shadows from design system
export const shadows = {
  sm: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
  md: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
} as const;

// Typography from design system
export const typography = {
  fontFamily: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    none: 1,
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
} as const;

// Spacing scale
export const spacing = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  14: '56px',
  16: '64px',
  20: '80px',
} as const;

// Border radius
export const borderRadius = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
} as const;
